import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { BellRinging } from '@phosphor-icons/react';

function NotificationSettings() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/notification-rules');
      setRules(data || []);
    } catch (e) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (event_key, enabled) => {
    // Optimistic UI
    setRules((prev) => prev.map((r) => r.event_key === event_key ? { ...r, enabled } : r));
    try {
      await api.put(`/api/admin/notification-rules/${encodeURIComponent(event_key)}`, { enabled });
      toast.success(`Rule "${event_key}" ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      // Revert
      setRules((prev) => prev.map((r) => r.event_key === event_key ? { ...r, enabled: !enabled } : r));
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-zinc-950">Auto-Notification Settings</h1>
          <p className="text-sm text-zinc-500 mt-1">Enable or disable automatic WhatsApp messages by event. Manual sends always work regardless of these toggles.</p>
        </div>

        <Card className="border-zinc-300 rounded-sm">
          <CardHeader className="border-b border-zinc-300">
            <CardTitle className="text-lg flex items-center gap-2"><BellRinging size={18} /> Event Rules</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-300">
                <tr>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Event Key</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Channels</th>
                  <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Last Updated</th>
                  <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Auto-Send</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                ) : rules.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center text-zinc-400">No rules</td></tr>
                ) : rules.map((r) => (
                  <tr key={r.event_key} className="border-b border-zinc-200" data-testid={`rule-row-${r.event_key}`}>
                    <td className="p-3 font-mono text-xs">{r.event_key}</td>
                    <td className="p-3">
                      {(r.channels || []).map((c) => <Badge key={c} variant="outline" className="text-[10px] uppercase mr-1">{c}</Badge>)}
                    </td>
                    <td className="p-3 text-xs text-zinc-500">{r.updated_at ? new Date(r.updated_at).toLocaleString() : 'Default'}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`text-[10px] font-bold uppercase ${r.enabled ? 'text-emerald-600' : 'text-zinc-400'}`} data-testid={`rule-state-${r.event_key}`}>
                          {r.enabled ? 'ON' : 'OFF'}
                        </span>
                        <Switch
                        data-testid={`toggle-${r.event_key}`}
                        checked={r.enabled}
                        onCheckedChange={(checked) => toggle(r.event_key, checked)}
                        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-zinc-300"
                      />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default NotificationSettings;
export { NotificationSettings };
